import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHotel } from "@/hooks/useHotel";
import { useCurrentHotelId } from "@/hooks/useCurrentHotelId";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { RoomStatus, Room } from "@/types/room";
import type { DateRange } from "@/hooks/useDateRange";
import RoomFilter from "./rooms/components/RoomFilter";
import { Edit, Trash2, Image, Plus, QrCode } from "lucide-react";
import { SubNavigation } from "./components/SubNavigation";
import { useRoomDelete } from "./rooms/hooks/useRoomDelete";
import { useRoomFiltering } from "@/hooks/useRoomFiltering";
import { DataTable, Column } from "@/components/ui/data-table";
import { RoomStatusLabel } from "./rooms/components/StatusLabel";
import { ReservationNavigation } from "./components/ReservationNavigation";
import { PresignedImageWithPlaceholder } from "@/components/common/PresignedImageWithPlaceholder";
import { RoomQrDialog } from "./rooms/components/RoomQrDialog";
import { createDefaultRoomUsageDateRange } from "@/lib/roomUsage";
import { RoomsAnalyticsOverview } from "./rooms/components/RoomsAnalyticsOverview";
import { useAccessCapabilities } from "@/hooks/useAccessCapabilities";
import { useGetPmsSettings } from "@/components/pages/pms/hooks/pms.hooks";
import { shouldShowPmsColumns } from "@/utils/pmsVisibility";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import { Checkbox } from "@/components/ui/checkbox";
import {
  shouldShowStatusColumn,
  shouldShowStatusFilter,
  shouldSortBeReset,
  shouldStatusFilterBeReset,
} from "./rooms/utils/roomTableColumns";
import { ALL_STATUSES } from "./rooms/utils/roomFilterDraft";
import {
  getHeaderState,
  pruneSelection,
  toggleAll,
  toggleRoom,
} from "./rooms/utils/roomSelection";

export const RoomsPage = () => {
  const { isLoading, error } = useHotel();
  const hotelId = useCurrentHotelId();

  if (isLoading && !hotelId) {
    return <div>Loading</div>;
  }

  if (error) {
    return (
      <div className="py-4 text-center text-red-600">
        Error loading hotel: {error.message}
      </div>
    );
  }

  if (!hotelId) {
    return <div>Loading</div>;
  }

  return <HotelRooms hotelId={hotelId} />;
};

export const HotelRooms: React.FC<{ hotelId: string }> = ({ hotelId }) => {
  const navigate = useNavigate();
  const { hasReservationsAccess } = useAccessCapabilities();
  const pmsSettings = useGetPmsSettings(hotelId);
  const showPmsColumns = shouldShowPmsColumns(pmsSettings);
  const [activeTab] = useState<"rooms" | "categories">("rooms");
  const [usageDateRange, setUsageDateRange] = useState<DateRange>(() =>
    createDefaultRoomUsageDateRange(),
  );

  // Define tabs for SubNavigation
  const subNavigationTabs = [
    { id: "rooms", label: "Rooms" },
    { id: "categories", label: "Categories" },
  ];

  const { rooms, isLoading, error, filters, actions, pagination, selection } =
    useRoomFiltering(hotelId!);

  const { userProfile } = useAuthenticatedUser();
  const showStatusColumn = shouldShowStatusColumn(userProfile);
  const showStatusFilter = shouldShowStatusFilter(userProfile);
  const resetStatusSort = shouldSortBeReset(userProfile, filters.sortField);
  const resetStatusFilter = shouldStatusFilterBeReset(
    userProfile,
    filters.status,
  );

  useEffect(() => {
    if (resetStatusSort) {
      actions.setSortField(null);
      actions.setSortDirection(null);
    }
  }, [resetStatusSort, actions]);

  useEffect(() => {
    if (resetStatusFilter) {
      actions.handleFilterChange(actions.setStatus, ALL_STATUSES);
    }
  }, [resetStatusFilter, actions]);

  const { handleRoomDelete, isLoading: isLoadingDeletion } = useRoomDelete();

  const { t } = useTranslation();
  const selectedRoomIds = selection.selectedRooms;
  const setSelectedRoomIds = actions.setSelectedRooms;
  const visibleRoomIds = useMemo(() => rooms.map((room) => room.id), [rooms]);
  const headerState = getHeaderState(selectedRoomIds, visibleRoomIds);

  const headerChecked: boolean | "indeterminate" =
    headerState === "some" ? "indeterminate" : headerState === "all";

  useEffect(() => {
    const pruned = pruneSelection(selectedRoomIds, visibleRoomIds);
    if (pruned !== selectedRoomIds) setSelectedRoomIds(pruned);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleRoomIds]);

  const columns: Column<Room>[] = [
    {
      key: "select",
      header: (
        <Checkbox
          className="size-5 border-zinc-900"
          checked={headerChecked}
          onCheckedChange={() =>
            setSelectedRoomIds(toggleAll(selectedRoomIds, visibleRoomIds))
          }
          aria-label={t("reservation.roomsTable.selectAll")}
        />
      ),
      className: "text-center w-10",
      render: (_, room: Room) => (
        <Checkbox
          className="size-5 border-zinc-900"
          checked={selectedRoomIds.includes(room.id)}
          onCheckedChange={() =>
            setSelectedRoomIds(toggleRoom(selectedRoomIds, room.id))
          }
          onClick={(event) => event.stopPropagation()}
          aria-label={t("reservation.roomsTable.selectRoom", {
            number: room.number,
          })}
        />
      ),
    },
    ...(showStatusColumn
      ? [
          {
            key: "status",
            header: "Status",
            className: "text-center",
            render: (value: RoomStatus) => <RoomStatusLabel status={value} />,
            sortable: true,
          },
        ]
      : []),
    {
      key: "photo",
      header: "Photo",
      className: "text-center flex justify-center items-center",
      render: (_, room: Room) => (
        <div className="flex h-12 w-12 items-center justify-center rounded border border-gray-300 bg-gray-100">
          <PresignedImageWithPlaceholder
            className={"h-10 w-10 overflow-hidden object-cover"}
            photo={room.photoId}
            placeholder={<Image className="h-6 w-6 text-gray-400" />}
          />
        </div>
      ),
    },
    {
      key: "categoryId",
      header: "Category",
      className: "text-center",
      render: (_, room: Room) => (
        <span className="text-sm">{room.category?.name}</span>
      ),
      sortable: true,
    },
    ...(showPmsColumns
      ? [
          {
            key: "amenities",
            header: "PMS Category",
            className: "text-center",
            render: (_: unknown, room: Room) => (
              <span>{room?.category?.pmsType ?? ""}</span>
            ),
          },
        ]
      : []),
    {
      key: "number",
      header: "Room",
      className: "text-center",
      sortable: true,
      render: (value: number) => <span>{value}</span>,
    },
    {
      key: "floor",
      header: "Floor",
      className: "text-center",
      sortable: true,
      render: (value: number) => <span>{value}</span>,
    },
    {
      key: "actions",
      header: "Actions",
      className: "text-center",
      render: (_, row) => (
        <div className="flex items-center justify-center gap-2">
          <RoomQrDialog
            room={row}
            hotelId={hotelId}
            trigger={
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={(event) => event.stopPropagation()}
              >
                <QrCode className="h-4 w-4" />
              </Button>
            }
          />
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={(event) => {
              event.stopPropagation();
              navigate(`/reservations/rooms/${row.id}`);
            }}
          >
            <Edit className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={(event) => {
              event.stopPropagation();
              handleDeleteRoom(row.id);
            }}
            disabled={isLoadingDeletion}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  // Handle room deletion
  const handleDeleteRoom = async (roomId: string) => {
    if (window.confirm("Are you sure you want to delete this room?")) {
      try {
        await handleRoomDelete(roomId, hotelId);
      } catch (error) {
        console.error("Failed to delete room:", error);
      }
    }
  };

  const onTabChange = (tab: string) => {
    if (tab === "categories") {
      navigate("/reservations/rooms/categories");
    }
  };

  return (
    <div className="space-y-6 p-12">
      {/* Navigation Tabs */}
      <ReservationNavigation activeTab="rooms" />

      {/* Sub Navigation */}
      <SubNavigation
        activeTab={activeTab}
        onTabChange={onTabChange}
        tabs={subNavigationTabs}
      />

      {hasReservationsAccess && showPmsColumns ? (
        <RoomsAnalyticsOverview
          hotelId={hotelId}
          dateRange={usageDateRange}
          onDateRangeChange={setUsageDateRange}
        />
      ) : null}

      {/* Filters and Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <RoomFilter
            hotelId={hotelId}
            actions={actions}
            filters={filters}
            showStatusFilter={showStatusFilter}
          />
        </div>

        <div className="flex items-center gap-2">
          {/* Task 0041, contract 2026-09-30 — hotel-wide/reception QR, next
              to each room's own (per-row, below). Same builder/PDF/print,
              no roomId. */}
          <RoomQrDialog
            room={null}
            hotelId={hotelId}
            trigger={
              <Button variant="outline">
                <QrCode />
                Hotel QR (reception)
              </Button>
            }
          />
          <Button
            variant="new_default"
            onClick={() => navigate("/reservations/rooms/add")}
          >
            <Plus />
            Add room
          </Button>
        </div>
      </div>

      {/* Data Table */}
      <DataTable
        data={rooms}
        columns={columns}
        loading={isLoading}
        onRowClick={(row) => navigate(`/reservations/rooms/${row.id}`)}
        pagination={{
          currentPage: pagination.currentPage,
          totalPages: pagination.totalPages,
          onPageChange: pagination.goToPage,
        }}
        sorting={{
          field: resetStatusSort ? null : filters.sortField,
          direction: filters.sortDirection,
          onSort: actions.handleSort,
        }}
        emptyMessage="No rooms found matching your criteria"
      />

      {error && (
        <div className="py-4 text-center text-red-600">
          Error loading rooms: {error.message}
        </div>
      )}
    </div>
  );
};
